import { DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { StripeService } from '../../services/stripe.service';
import { SITE_ACCESS_PAGES, SiteAccess, SiteAccessService } from '../../services/site-access.service';
import { isLockedFor } from '../../services/site-access.logic';

@Component({
  selector: 'app-header',
  standalone: false,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {

  isScrolled = false;
  mobileOpen = false;
  userMenuOpen = false;
  adminMenuOpen = false;
  portalLoading = false;
  portalError = '';
  private levels: Record<string, SiteAccess> = {};

  /**
   * A page this visitor cannot open yet. Its link stays visible either way, so people can see how much there is, with a lock
   * tag; clicking it shows a preview of the page. Admin only pages are locked for everyone except admins.
   */
  isLocked(pageId: string): boolean {
    const page = SITE_ACCESS_PAGES.find(p => p.id === pageId);
    const level = this.levels[pageId];
    if (!page || !level) return false;
    return isLockedFor(page, level, {
      signedIn: !!this.auth.currentUser,
      isAdmin: this.auth.isAdmin,
      hasFeature: key => this.auth.hasFeature(key),
      canAccessLevelData: this.auth.canAccessLevelData,
    });
  }


  private scrollObserver?: IntersectionObserver;
  private scrollSentinel?: HTMLElement;

  constructor(
    public auth: AuthService,
    private stripe: StripeService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private siteAccess: SiteAccessService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.userMenuOpen && !(event.target as HTMLElement).closest('.nav-user')) {
      this.userMenuOpen = false;
    }
    if (this.adminMenuOpen && !(event.target as HTMLElement).closest('.nav-admin')) {
      this.adminMenuOpen = false;
    }
  }

  toggleUserMenu(): void {
    this.userMenuOpen = !this.userMenuOpen;
    this.portalError = '';
  }

  toggleAdminMenu(): void {
    this.adminMenuOpen = !this.adminMenuOpen;
  }

  async manageSubscription(): Promise<void> {
    this.portalLoading = true;
    this.portalError = '';
    try {
      await this.stripe.manageSubscription();
      // Success navigates the browser away — nothing left to reset here.
    } catch (error) {
      console.error('manageSubscription failed', error);
      this.portalError = error instanceof Error ? error.message : 'Could not open billing portal.';
      this.portalLoading = false;
      this.cdr.markForCheck();
    }
  }

  private static readonly INSIGHTS_MARKET_TABS = ['next-day-bias', 'move-map'];

  get isInsightsActive(): boolean {
    return this.router.url.startsWith('/insights/');
  }

  get isAdminActive(): boolean {
    return this.router.url.startsWith('/admin')
      || this.router.url.startsWith('/insights/')
      || this.router.url.startsWith('/employee/');
  }

  get isIndexesActive(): boolean {
    const url = this.router.url;
    if (!url.startsWith('/market-breadth')) return false;
    const tab = new URLSearchParams(url.split('?')[1] ?? '').get('tab') ?? '';
    return !HeaderComponent.INSIGHTS_MARKET_TABS.includes(tab);
  }

  // The link opens the desk (QQQ Options tab), but Strategies lives on the parent path,
  // so routerLinkActive on the /desk link alone would go dark there.
  get isOptionsDeskActive(): boolean {
    return this.router.url.startsWith('/options-desk') || this.router.url.startsWith('/QQQ-Options-Strategies');
  }

  ngOnInit(): void {
    void this.siteAccess.levels().then(levels => {
      this.levels = levels;
      this.cdr.detectChanges();
    });
    this.setupScrollObserver();
  }

  ngOnDestroy(): void {
    this.scrollObserver?.disconnect();
    this.scrollSentinel?.remove();
  }

  toggleMobile(): void {
    this.mobileOpen = !this.mobileOpen;
  }

  closeMobile(): void {
    this.mobileOpen = false;
  }

  private setupScrollObserver(): void {
    if (typeof IntersectionObserver === 'undefined' || !this.document.body) {
      return;
    }

    this.scrollSentinel = this.document.createElement('span');
    this.scrollSentinel.setAttribute('aria-hidden', 'true');
    this.scrollSentinel.className = 'header-scroll-sentinel';
    Object.assign(this.scrollSentinel.style, {
      position: 'absolute',
      top: '50px',
      left: '0',
      width: '1px',
      height: '1px',
      pointerEvents: 'none',
      opacity: '0'
    });

    this.document.body.prepend(this.scrollSentinel);

    this.zone.runOutsideAngular(() => {
      this.scrollObserver = new IntersectionObserver(([entry]) => {
        this.setScrolledState(!entry.isIntersecting);
      }, { threshold: 0 });

      this.scrollObserver.observe(this.scrollSentinel as HTMLElement);
    });
  }

  private setScrolledState(next: boolean): void {
    if (next === this.isScrolled) {
      return;
    }

    this.zone.run(() => {
      this.isScrolled = next;
      this.cdr.markForCheck();
    });
  }
}
